import React, { useEffect, useState } from "react";
import "./AddOrganization.scss";
import { useForm } from "react-hook-form";
import InputField from "../../../components/InputField/InputField";
import { useLocation, useNavigate } from "react-router-dom";
import ROUTES from "../../../constants/routes";
import CustomButton from "../../../components/CustomButton/CustomButton";
import { addOrganization, updateOrganization } from "../../../api/organization";
import {
  updateOrganization as updateOrganizationRedux,
  updateOrganizationSubscription,
} from "../../../redux/features/organization-slice";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import {
  updateActiveBranch,
  updateBranches,
} from "../../../redux/features/branch-slice";
import useOrganizationIsAllowed from "../../../helpers/organizationPrevillages";
import {
  updateFreeTrailStatus,
  updateOpenSubscriptionAd,
} from "../../../redux/features/subscription-slice";
import { updateIsNewOrg } from "../../../redux/features/tour-slice";
import { IoIosArrowBack, IoMdCloseCircle } from "react-icons/io";
import { CountryCodes } from "../../../constants/countryCodes";
import InputSelect from "../../../components/InputSelect/InputSelect";
import useUserIsAllowed from "../../../helpers/userAuthorized";
import { storage } from "../../../helpers/storage";
import ImageInput from "../../../components/ImageInput/ImageInput";

const IMAGE_URL = process.env.REACT_APP_IMAGE_URL;

const AddOrganization = ({ isEdit }) => {
  const dispatch = useDispatch();
  const { id: original_organization_id, countryCode } = useSelector(
    (state) => state.organizationReducer.value
  );
  const { id: original_branch_id, branches } = useSelector(
    (state) => state.branchReducer.value
  );
  const location = useLocation();
  const navigate = useNavigate();
  const editData = isEdit ? location.state?.editData : {};
  const organization_id = isEdit ? editData?.id : null;

  const orgIsAllowedToAddCustomLogo = useOrganizationIsAllowed(
    "upload_product_photo"
  );

  const userIsAllowedToViewOrganization = useUserIsAllowed("administrator");

  const [image, setImage] = useState({
    src: null,
    file: null,
  });

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({});

  const [submitting, setSubmitting] = useState(false);

  const [options, setOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState({
    label: "",
    value: "",
  });

  useEffect(() => {
    if (!isEdit && original_organization_id) {
      navigate(ROUTES.myOrganization.path);
    }
  }, [original_organization_id, isEdit]);

  useEffect(() => {
    const selectOptions = [];
    CountryCodes.forEach((item) => {
      selectOptions.push({
        label: `${item.flag}(${item.country_code})`,
        value: item.country_code,
      });
    });

    const orgDefault = selectOptions.find((el) => el?.value === countryCode);

    setSelectedOption(orgDefault || selectOptions[0]);
    setOptions(selectOptions);
  }, []);

  useEffect(() => {
    if (isEdit) {
      if (!organization_id) {
        navigate(ROUTES.myOrganization.path);
      } else {
        reset({
          name: editData?.name,
          address: editData?.address,
          phone: editData?.phone,
          email: editData?.email,
          country_code: editData?.country_code,
          description: editData?.description,
        });
        if (editData?.image) {
          setImage({
            file: null,
            src: `${IMAGE_URL}/${editData?.image}`,
          });
        }
      }
    } else if (original_organization_id) {
      navigate(ROUTES.myOrganization.path);
    }
  }, [organization_id, isEdit]);

  const handleAddOrganixation = (data) => {
    const organizationData = new FormData();
    organizationData.append("name", data?.name);
    organizationData.append("country_code", selectedOption?.value);
    if (data?.address) {
      organizationData.append("address", data?.address);
    }
    if (data?.phone) {
      organizationData.append("phone", data?.phone);
    }
    if (data?.email) {
      organizationData.append("email", data?.email);
    }
    if (image.file) {
      organizationData.append("image", image.file);
    } else if (!image.src) {
      organizationData.append("image_remove", true);
    }

    if (data?.description) {
      organizationData.append("description", data?.description);
    }

    setSubmitting(true);

    if (!isEdit) {
      addOrganization(organizationData)
        .then((res) => {
          if (res.data?.organization && !original_organization_id) {
            storage.set(
              "organization",
              JSON.stringify({
                ...res.data?.organization,
                currency: res.data?.organization?.currency_symbol,
                currencyCode: res.data?.organization?.currency,
              })
            );
            dispatch(
              updateOrganizationRedux({
                ...res.data?.organization,
                currency: res.data?.organization?.currency_symbol,
                currencyCode: res.data?.organization?.currency,
              })
            );
          }

          const organizationSubscription =
            res.data?.organization?.subscription || {};

          storage.set(
            "organizationSubscription",
            JSON.stringify(organizationSubscription)
          );
          dispatch(
            updateOrganizationSubscription({
              subscription: organizationSubscription,
            })
          );

          if (organizationSubscription?.free_trial) {
            dispatch(updateFreeTrailStatus({ free_trail: true }));
          }

          if (res.data?.branch) {
            if (!original_branch_id) {
              storage.set("activeBranch", JSON.stringify(res.data?.branch));
              dispatch(updateActiveBranch(res.data?.branch));
            }
            storage.set(
              "branches",
              JSON.stringify([...branches, res.data?.branch])
            );
            dispatch(
              updateBranches({ branches: [...branches, res.data?.branch] })
            );
          }
          toast.dismiss();
          toast.success("New organization added.");
          navigate(ROUTES.myOrganization.path);
          dispatch(updateIsNewOrg(true));
        })
        .catch((err) => {
          if (err?.response?.data?.message) {
            if (err?.response?.data?.message === "User not authorized!") {
              toast.dismiss();
              toast.error(
                "You do not have sufficient permissions to perform this action. Please contact your administrator."
              );
            } else {
              toast.dismiss();
              toast.error(`${err?.response?.data?.message}`);
            }
          } else {
            toast.dismiss();
            toast.error("Error while adding new organization!");
          }
        })
        .finally(() => {
          setSubmitting(false);
        });
    } else {
      updateOrganization(organizationData, organization_id)
        .then((res) => {
          if (
            res.data?.organization &&
            organization_id === original_organization_id
          ) {
            const organization = res.data?.organization;
            storage.set(
              "organization",
              JSON.stringify({
                ...organization,
                currency: organization?.currency_symbol,
                currencyCode: organization?.currency,
              })
            );
            dispatch(
              updateOrganizationRedux({
                ...organization,
                currency: organization?.currency_symbol,
                currencyCode: organization?.currency,
              })
            );
          }
          toast.dismiss();
          toast.success("Organization updated successfully.");
          navigate(ROUTES.myOrganization.path);
        })
        .catch((err) => {
          if (err?.response?.data?.message) {
            if (err?.response?.data?.message === "User not authorized!") {
              toast.dismiss();
              toast.error(
                "You do not have sufficient permissions to perform this action. Please contact your administrator."
              );
            } else {
              toast.dismiss();
              toast.error(`${err?.response?.data?.message}`);
            }
          } else {
            toast.dismiss();
            toast.error("Error while updating organization!");
          }
        })
        .finally(() => {
          setSubmitting(false);
        });
    }
  };
  return (
    <div className="page-with-side-components">
      <div className="desktop-center-components">
        <div className="desktop-navbar box-shadow payment-navbar">
          {isEdit && (
            <div
              className="back"
              onClick={() => {
                navigate(ROUTES.myOrganization.path);
              }}
            >
              <IoIosArrowBack />
            </div>
          )}
          <p>
            {isEdit
              ? ROUTES.editOrganization.title
              : ROUTES.addOrganization.title}
          </p>
        </div>
        <div className="add-organization">
          <form
            aria-disabled={!userIsAllowedToViewOrganization}
            className="container-wrapper"
            onSubmit={handleSubmit(handleAddOrganixation)}
          >
            <div className="form-container">
              {isEdit && (
                <ImageInput
                  image={image}
                  setImage={setImage}
                  label={"Organization's Image"}
                  featureAvailable={orgIsAllowedToAddCustomLogo.exists}
                  isAllowed={true}
                />
              )}
              <InputField
                name={"name"}
                type={"text"}
                label={"Organization Name*"}
                required={true}
                register={register}
                inputErrors={errors}
                watch={watch}
                disabled={!userIsAllowedToViewOrganization}
              />

              <InputField
                name={"email"}
                type={"email"}
                label={"Email"}
                required={false}
                register={register}
                inputErrors={errors}
                watch={watch}
                disabled={!userIsAllowedToViewOrganization}
              />

              <InputField
                name={"address"}
                type={"text"}
                label={"Address"}
                required={false}
                register={register}
                inputErrors={errors}
                watch={watch}
                disabled={!userIsAllowedToViewOrganization}
              />

              <div className="select-and-input">
                <InputField
                  name={"phone"}
                  type={"text"}
                  label={"Phone Number"}
                  required={false}
                  register={register}
                  inputErrors={errors}
                  watch={watch}
                  constraints={{
                    maxLength: {
                      value: 15,
                      message: "Phone number cannot exceed 15 digits",
                    },
                    pattern: {
                      value: /^[0-9]+$/,
                      message: "Only numbers are allowed",
                    },
                  }}
                  disabled={!userIsAllowedToViewOrganization}
                />
                <div className="select-wrapper">
                  <InputSelect
                    options={options}
                    value={selectedOption}
                    register={register}
                    required={false}
                    name="country_code"
                    handleChange={(opt) => {
                      setSelectedOption(opt);
                    }}
                    reset={reset}
                    label={""}
                    inputErrors={errors}
                    disabled={!userIsAllowedToViewOrganization}
                  />
                </div>
              </div>

              <InputField
                name={"description"}
                type={"text"}
                label={"About Us"}
                required={false}
                register={register}
                inputErrors={errors}
                watch={watch}
                textArea={true}
                disabled={!userIsAllowedToViewOrganization}
              />

              <CustomButton
                name={isEdit ? "Edit Organization" : "Create Organization"}
                color={"primary"}
                type={"submit"}
                handleSubmit={() => {}}
                disabled={submitting || !userIsAllowedToViewOrganization}
                bigButton={true}
                id={"tour-add-organization"}
                loader={submitting}
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddOrganization;
